import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from "react";

import { FOCUS_RING_CLASS } from "@/lib/a11y";
import { cn } from "@/lib/utils";

type Tone = "neutral" | "danger" | "approve" | "reject";

const TONE_CLASS: Record<Tone, string> = {
  neutral: "hover:border-[#ffc83d] hover:text-[#08254c]",
  danger: "hover:border-[#d4353c] hover:text-[#d4353c]",
  // Decisão de escopo (Gerenciamento): aprovar e tirar do escopo ganham cor própria.
  approve:
    "border-[rgba(39,161,85,0.25)] bg-[rgba(39,161,85,0.08)] text-[#1e7d42] hover:border-[var(--exed-success)] hover:bg-[var(--exed-success)] hover:text-white",
  reject:
    "border-[rgba(212,53,60,0.22)] bg-[rgba(212,53,60,0.06)] text-[#a3262c] hover:border-[var(--exed-danger)] hover:bg-[var(--exed-danger)] hover:text-white",
};

/**
 * Ação de linha de tabela/lista: quadrado de 32px com borda, só o ícone.
 *
 * É o formato que as linhas do projeto já usavam para "ver detalhes"
 * (árvore organizacional, downloads de artefato); cada tela reescrevia as
 * classes à mão, e na mesma linha acabavam convivendo botão com rótulo,
 * ícone solto e quadrado com borda. Uma linha com várias ações usa só este.
 *
 * O `label` é obrigatório porque não há texto visível: ele vira o nome
 * acessível e a dica nativa. `title` sobrescreve só a dica — serve para
 * explicar por que a ação está desabilitada sem trocar o nome dela.
 *
 * Sem `TOUCH_TARGET_CLASS`: ele estende a área 8px para cada lado, e com
 * ações vizinhas a 6px uma engoliria o clique da outra.
 */
export const RowActionButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
    label: string;
    icon: ReactNode;
    tone?: Tone;
  }
>(function RowActionButton(
  { label, icon, tone = "neutral", title, className, onClick, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title ?? label}
      data-row-action=""
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        // A linha/card em volta também reage a clique (selecionar, expandir).
        event.stopPropagation();
        onClick?.(event);
      }}
      className={cn(
        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#e8ebf0] bg-white text-[#596b85] transition-colors [&_svg]:h-3.5 [&_svg]:w-3.5",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#e8ebf0] disabled:hover:text-[#596b85]",
        TONE_CLASS[tone],
        FOCUS_RING_CLASS,
        className,
      )}
      {...props}
    >
      {icon}
    </button>
  );
});
